import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Connection,
  Edge,
  EdgeChange,
  NodeChange,
  ReactFlowInstance,
} from 'reactflow';
import { createRequestAtom, createRequestFamily } from '../../../shared/utils/request-atom';
import {
  AvailableChannelsResponse,
  Bot,
  BotListData,
  BotNode,
  BotStatus,
  ComparableData,
  DynamicMenuItem,
  HelpData,
  Integration,
  SubscribedPlan,
  WarningType,
} from '../typings';
import {
  ActionTypes,
  AppData,
  BlockDataType,
  ComponentVariables_,
  Element,
  GlobalVariable,
  IntegrationsType,
  NodeData,
  TimeZone,
  TriggerTypes,
  WebhooksValue,
} from '../typings/bot-data';
import { Template, TemplateIntegration } from '../typings/template';
import { getWorker } from '../../../infra/web-worker/instance';
import { wrap } from 'comlink';
import { APP_ID } from '../../../shared/constants';

const dtWorker = getWorker();
const dtWorkerApi = wrap<import('../../../infra/web-worker').DTWorker>(dtWorker);

export interface Param {
  id: string;
}

export const botFamilyAtom = atomFamily(
  (param: Param) =>
    atom<Bot>({
      id: param.id,
      nodes: [],
      edges: [],
      name: '',
      status: BotStatus.INACTIVE,
      dateCreated: '',
    }),
  (a: Param, b: Param) => a.id === b.id
);

export const triggerActionsMap = atom<Record<string, Element>>({});

export const botsListFamilyAtom = createRequestFamily<BotListData[]>();

export const appDataFamilyAtom = createRequestFamily<AppData | undefined>();

export const showZoomOutAtom = atom<string>('');

export const lastFetchedAppDataChannelAtom = atom<string | null>(null);

export const templatesDataAtom = createRequestAtom<Template[]>();

// Template Sent trigger: its own list (grouped per template), and each template's
// channels keyed by the template group key.
export const templateSentTemplatesDataAtom = createRequestAtom<Template[]>();

export const templateIntegrationsFamilyAtom = createRequestFamily<TemplateIntegration[]>();

// "On Template Delivered" V2: for each node, on how many of its approved channels each
// reply branch's button exists (branches are the union of the channels' buttons).
export const templateSentBranchCoverageFamilyAtom = atomFamily(
  (param: Param) => atom<Map<string, Record<string, { on: number; of: number }>>>(new Map()),
  (a: Param, b: Param) => a.id === b.id
);

export const botStartNode = atom<BotNode | undefined>(undefined);

export const warningsAtom = atom<WarningType>({
  show: false,
  message: '',
});

export const helpModalDataAtom = atom<HelpData | undefined>(undefined);
export const reactFlowInstanceAtom = atom<ReactFlowInstance | null>(null);

export interface SidebarComponentData {
  id: string;
  name: string;
  blockType: ActionTypes | TriggerTypes;
  blockDataType?: BlockDataType;
  isVisible?: boolean;
  helpData?: HelpData;
  description?: string;
  blockDescription?: string | null;
  blockGroup?: string;
  groupLabel?: string;
  groupColor?: string;
  groupIsIntegration?: boolean;
  groupVisibilityKey?: string | null;
  groupOrder?: number;
  groupBadge?: string | null;
}

export interface SidebarData {
  triggers: SidebarComponentData[];
  actions: SidebarComponentData[];
  // Absent on a backend that predates notes.
  planning?: SidebarComponentData[];
}

export const sidebarDataAtom = atom<SidebarData | undefined>(undefined);

export const selectedAppIdAtom = atom<string | undefined>(APP_ID);

export const webhookTriggerIdAtom = atom<string | undefined>(undefined);
export const webhookTriggerDataAtom = atom<WebhooksValue | undefined>(undefined);

export const selectedAppData = atom((get) => {
  const appId = get(selectedAppIdAtom);
  if (!appId) return undefined;
  const data = get(appDataFamilyAtom({ id: appId })).data;
  return data;
});

export const selectedBotIdAtom = atom<string | undefined>(undefined);

export const selectedBotData = atom((get) => {
  const botId = get(selectedBotIdAtom);
  if (!botId) return undefined;
  const data = get(botFamilyAtom({ id: botId }));
  return data;
});

export const botSetter = atom(null, (get, set, data: { data: Bot }) => {
  const { data: bot } = data;
  const botAtom = botFamilyAtom({ id: bot.id });
  set(botAtom, () => bot);
});

export const nodeChangeSetter = atom(null, (get, set, data: { nodes: NodeChange[] }) => {
  const botData = get(selectedBotData);
  if (!botData) return;
  const botAtom = botFamilyAtom({ id: botData.id });
  const { nodes } = data;
  set(botAtom, {
    ...botData,
    nodes: applyNodeChanges(nodes, botData.nodes),
  });
});

export const edgeChangeSetter = atom(null, (get, set, data: { edges: EdgeChange[] }) => {
  const botData = get(selectedBotData);
  if (!botData) return;
  const botAtom = botFamilyAtom({ id: botData.id });
  const { edges } = data;
  set(botAtom, {
    ...botData,
    edges: applyEdgeChanges(edges, botData.edges),
  });
});

export const nodeValuesFamilyAtom = atomFamily(
  (param: Param) => atom<ValuesMap>(new Map()),
  (a: Param, b: Param) => a.id === b.id
);

export const nodeNameFamilyAtom = atomFamily(
  (param: Param) => atom<Map<string, string>>(new Map()),
  (a: Param, b: Param) => a.id === b.id
);

export const nodeDynamicMenuItemsFamilyAtom = atomFamily(
  (param: Param) => atom<Map<string, DynamicMenuItem[]>>(new Map()),
  (a: Param, b: Param) => a.id === b.id
);

export const nodeIdToVariableValuesAtom = atom<Map<string, Set<string>>>(new Map<string, Set<string>>());

export const nodeVariablesFamilyAtom = atomFamily(
  (param: Param) => atom<ComponentVariables_>({}),
  (a: Param, b: Param) => a.id === b.id
);

export const nodeIntegrationsFamilyAtom = atomFamily(
  (param: Param) => atom<Map<string, IntegrationsType>>(new Map()),
  (a: Param, b: Param) => a.id === b.id
);

export const connectionSetter = atom(null, (get, set, data: { connection: Connection; edges: Edge[] }) => {
  const bot = get(selectedBotData);
  if (!bot) return;
  const botAtom = botFamilyAtom({ id: bot.id });
  const { connection, edges } = data;
  set(botAtom, {
    ...bot,
    edges: addEdge(connection, edges),
  });
});

export const botNameGetter = atom((get) => {
  const bot = get(selectedBotData);
  if (!bot) return '';
  return bot.name;
});

export const botStatusGetter = atom((get) => {
  const bot = get(selectedBotData);
  if (!bot) return BotStatus.INACTIVE;
  return bot.status;
});

export const edgeDeleteSetter = atom(null, async (get, set, data: { id: string }) => {
  const botId = get(selectedBotIdAtom);
  if (!botId) return;
  const botAtom = botFamilyAtom({ id: botId });
  const bot = get(botAtom);
  const { id } = data;
  const edges = await dtWorkerApi.deleteEdge(bot.edges, id);
  const newBot = {
    ...bot,
    edges,
  };
  set(botAtom, newBot);
});

export const addNodeSetter = atom(null, (get, set, data: { data: BotNode }) => {
  const botId = get(selectedBotIdAtom);
  if (!botId) return;
  const botAtom = botFamilyAtom({ id: botId });
  const bot = get(botAtom);
  const { data: node } = data;

  const isAction = node.data.blockDataType === BlockDataType.ACTION;
  if ((bot.nodes.length === 0 || !get(botStartNode)) && !isAction) {
    node.data.isStartNode = true;
  }

  if (node.data.isStartNode) {
    set(botStartNode, node);
  }

  const newBot = {
    ...bot,
    edges: [...bot.edges],
    nodes: [...bot.nodes, node],
  };
  set(botAtom, newBot);
});

export const nodeAsStartSetter = atom(null, async (get, set, data: { nodeId: string }) => {
  const botId = get(selectedBotIdAtom);
  if (!botId) return;
  const botAtom = botFamilyAtom({ id: botId });
  const bot = get(botAtom);

  const { nodeId } = data;
  const { edges, nodes, startNode } = await dtWorkerApi.nodeAsStartHelper({
    botData: bot,
    nodeId,
  });
  set(botStartNode, startNode);
  const newBot = {
    ...bot,
    nodes,
    edges,
  };
  set(botAtom, newBot);
});

export const nodeDataSetter = atom(null, async (get, set, data: { nodeId: string; nodeData: Partial<NodeData> }) => {
  const botId = get(selectedBotIdAtom);
  if (!botId) return;
  const botAtom = botFamilyAtom({ id: botId });
  const bot = get(botAtom);
  const { nodeId, nodeData } = data;
  const nodes = await dtWorkerApi.setNodeData(bot.nodes, nodeId, nodeData);
  const newBot = {
    ...bot,
    nodes,
  };
  set(botAtom, newBot);
});

export type ValuesMap = Map<string, Element>;

export const nodeErrorFamilyAtom = atomFamily(
  (param: Param) => atom<Map<string, boolean>>(new Map()),
  (a: Param, b: Param) => a.id === b.id
);

export const templateSelectorNodeErrorFamilyAtom = atomFamily(
  (param: Param) => atom<Set<string>>(new Set<string>()),
  (a: Param, b: Param) => a.id === b.id
);

export const currentGlobalVariableStateAtom = atom<GlobalVariable[]>([]);

type StackItem = {
  state: GlobalVariable[];
};

export const globalVariablesStateStackAtom = atom<StackItem[]>([]);

export const globalVariablesNameStackAtom = atom<string[]>([]);

export const currentGlobalVariableValueAtom = atom<GlobalVariable | null>(null);

export const showVariableListAtom = atom<{ selectedNode: string }>({
  selectedNode: '',
});

export const timezonesAtom = createRequestAtom<TimeZone[]>();

export const savedBotStateAtom = atom<ComparableData | undefined>(undefined);

export const autoDeleteNodeIdAtom = atom<string | undefined>(undefined);

export const subscribedPlanAtom = createRequestAtom<{
  subscribedPlan: SubscribedPlan;
}>();

export const globalSaveBotCallbackAtom = atom<(() => Promise<boolean | void>) | null>(null);

export const integrationDetailsAtom = createRequestAtom<Integration[]>();

// Channel atoms
export const availableChannelsAtom = createRequestAtom<AvailableChannelsResponse>();
export const selectedChannelAtom = atom<string | null>(null);
export const channelLoadingAtom = atom<boolean>(false);
export const isBotPersistedAtom = atom<boolean>(false);
export const incompatibleNodeIdsAtom = atom<Set<string>>(new Set<string>());
export const channelLockedAtom = atom((get) => get(isBotPersistedAtom));
export const toolJourneyDetailsAtom = atom<{
  isToolJourneyBot: boolean;
  hasPassToolCallResponse: boolean;
}>({
  isToolJourneyBot: false,
  hasPassToolCallResponse: false,
});
